/* eslint-disable no-console */
/* eslint-disable react/no-unescaped-entities */
import { CallMadeRounded } from '@mui/icons-material';
import { ErrorBoundary } from 'components/Errors/ErrorBoundary';
import LoadingScreen from 'components/LoadingScreen';
import Skeleton from 'components/Skeleton';
import { useAtomValue } from 'jotai';
import { isAdminUserLoggedIn } from 'lib/helper';
import React, { useState, useEffect, Suspense } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import Select from 'react-select';
import { ToastContainer } from 'react-toastify';
import { Row, Col, Button } from 'reactstrap';
import CreateActivationKeyModal from 'views/Company/Configuration/tabs/CAActivationKeysTab/CreateActivationKeyModal';
import { companyIdAtom } from 'views/state';

import styles from './styles.module.css';
import { verifyAccessToken } from '../../lib/auth';
import axios from '../../lib/axiosInstance';

const osOptions = [
  { value: 'windows', label: 'Windows' },
  { value: 'macos_x64', label: 'MacOS (x64)' },
  { value: 'macos_arm64', label: 'MacOS (ARM64)' },
  { value: 'linux_rpm', label: 'Linux (.rpm x64)' },
  { value: 'linux_rpm_arm64', label: 'Linux (.rpm ARM64)' },
  { value: 'linux_deb', label: 'Linux (.deb x64)' },
  { value: 'linux_deb_arm64', label: 'Linux (.deb ARM64)' },
  { value: 'aix', label: 'AIX' },
  { value: 'bsd', label: 'BSD' },
];

const agentInstallerWindows =
  'https://cloud-agent-installer.s3.amazonaws.com/revised/QualysCloudAgent6.4.0.397-Windows.zip';
const agentInstallerMAC =
  'https://cloud-agent-installer.s3.amazonaws.com/revised/QualysCloudAgent6.2.1.19-MacOS-x64.zip';
const agentInstallerMAC_ARM64 =
  'https://cloud-agent-installer.s3.amazonaws.com/revised/QualysCloudAgent6.3.1.23-MacOS-ARM64.zip';
const agentInstallerLinuxRPM =
  'https://cloud-agent-installer.s3.amazonaws.com/revised/QualysCloudAgent7.3.0.40-Linux-RPM-x64.zip';
const agentInstallerLinuxRPM_ARM64 =
  'https://cloud-agent-installer.s3.amazonaws.com/revised/QualysCloudAgent6.1.1.11-Linux-RPM-ARM64.zip';
const agentInstallerLinuxDEB =
  'https://cloud-agent-installer.s3.amazonaws.com/revised/QualysCloudAgent7.3.0.40-Linux-Deb-x64.zip';
const agentInstallerLinuxDEB_ARM64 =
  'https://cloud-agent-installer.s3.amazonaws.com/revised/QualysCloudAgent6.1.1.11-Linux-Deb-ARM64.zip';
const agentInstallerAIX =
  'https://cloud-agent-installer.s3.amazonaws.com/revised/QualysCloudAgent%2B6.0.0.22-AIX.bff.zip';
const agentInstallerBSD =
  'https://cloud-agent-installer.s3.amazonaws.com/revised/QualysCloudAgent%2B7.1.0.13-BSD.zip';

const GuestCAInstallationInstructionsContent = () => {
  const [companyName, setCompanyName] = useState(
    localStorage.getItem('currentCompany').replaceAll('"', ''),
  );
  const [customerId, setCustomerId] = useState(null);
  const [loader, setLoader] = useState(false);
  const [selectedOS, setSelectedOS] = useState(null);
  const [selectedActivationKey, setSelectedActivationKey] = useState(null);
  const [agentActivationKeyOptions, setAgentActivationKeyOptions] = useState(
    [],
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newKey, setNewKey] = useState(null);
  const companyId = useAtomValue(companyIdAtom);

  // Toggle modal
  const toggleCreateKeyModal = () => setIsModalOpen(!isModalOpen);

  // Get existing key names to prevent duplicates
  const existingKeyNames = agentActivationKeyOptions.map(
    (option) => option.label,
  );

  /**
   * Handle successful creation of a new activation key
   * @param {Object} response - The API response containing the newly created key
   */
  const handleKeyCreationSuccess = (response) => {
    // Store the new key data from the response
    if (response && response.data) {
      setNewKey(response.data);
    }

    // Refresh the activation keys list
    void fetchData();

    // Close the modal
    setIsModalOpen(false);
  };

  const fetchData = async () => {
    setLoader(true);
    await verifyAccessToken();
    let url = '/cloud_agents/activation-keys/' + companyId + '/';
    let headers = {
      Authorization: 'Bearer ' + localStorage.getItem('access'),
    };

    try {
      const res = await axios.get(url, { headers: headers });
      if (res.status === 201 || res.status === 200) {
        const data = res.data;
        setCustomerId(data.customerId);
        const activationKeyOptions = Object.entries(data)
          .filter(([key]) => key !== 'customerId') // Exclude customerId if not needed
          .map(([key, value]) => ({
            value,
            label: key.replace(/^./, (str) => str.toUpperCase()), // Format label
          }));
        setAgentActivationKeyOptions(activationKeyOptions);

        // If we just created a new key, try to select it by finding a match in the title
        if (newKey && activationKeyOptions.length > 0) {
          const newKeyOption = activationKeyOptions.find((option) =>
            option.label.toLowerCase().includes(newKey.title?.toLowerCase()),
          );
          if (newKeyOption) {
            setSelectedActivationKey(newKeyOption);
          }
        }

        setCompanyName(
          localStorage.getItem('currentCompany').replaceAll('"', ''),
        );
      }
    } catch (error) {
      console.error('Error fetching activation keys:', error);
    } finally {
      setLoader(false);
    }
  };

  // Function to reset all state variables
  const resetState = () => {
    setCustomerId(null);
    setLoader(false);
    setSelectedOS(null);
    setSelectedActivationKey(null);
    setAgentActivationKeyOptions([]);
    setIsModalOpen(false);
    setNewKey(null);
    // Update company name from localStorage
    setCompanyName(localStorage.getItem('currentCompany').replaceAll('"', ''));
  };

  useEffect(() => {
    const handleCompanyChange = async () => {
      if (companyId) {
        // First reset all state variables
        resetState();

        // Then fetch new data after state has been reset
        await fetchData();
      }
    };

    void handleCompanyChange();
  }, [companyId]);

  const handleOSChange = (selectedOption) => {
    setSelectedOS(selectedOption);
  };

  const handleActivationKeyChange = (selectedOption) => {
    setSelectedActivationKey(selectedOption);
  };

  const getInstallationInstructions = () => {
    const activationKey = selectedActivationKey.value;
    if (!selectedOS || !activationKey) return null;

    const instructions = {
      windows: {
        text: (
          <div className="d-flex flex-column" style={{ gap: '5px' }}>
            <h4 style={{ margin: 0, padding: 0 }}>
              Windows Installer (.exe) - {companyName} -{' '}
              {selectedActivationKey.label}
            </h4>
            <ol>
              <li>Download the agent installer</li>
              <li>
                Copy <b>QualysCloudAgent.exe</b> to the host you want to monitor
                and run command
              </li>
              <li>Copy paste this command:</li>
              <li className="d-flex">
                <code>
                  <p>
                    {`QualysCloudAgent.exe CustomerId={${customerId}} ActivationId={${activationKey}} WebServiceUri=https://qagpublic.qg3.apps.qualys.com/CloudAgent/`}
                    <CopyButton
                      text={`QualysCloudAgent.exe CustomerId={${customerId}} ActivationId={${activationKey}} WebServiceUri=https://qagpublic.qg3.apps.qualys.com/CloudAgent/`}
                    />
                  </p>
                </code>
              </li>
              <li className="d-flex">
                Note: The Activation Key will vary depending on which
                environment the Cloud Agent is being deployed.
              </li>
            </ol>
          </div>
        ),
        downloadUrl: agentInstallerWindows,
      },
      macos_x64: {
        text: (
          <div className="d-flex flex-column" style={{ gap: '5px' }}>
            <h4 style={{ margin: 0, padding: 0 }}>
              Mac (.pkg) - x64 - {companyName} - {selectedActivationKey.label}
            </h4>
            <p>OS X / macOS</p>
            <ol>
              <li>Download the agent installer</li>
              <li>Copy QualysCloudAgent.pkg to the host you want to monitor</li>
              <li>Install the Cloud Agent Package</li>
              <li>
                Open Finder -&gt;Application -&gt; Click on the Qualys App and
                click the app to select “terminal from this folder”.
              </li>
              <li>Enter the following command (Sudo access required):</li>
              <li className="d-flex">
                <code>
                  <p>
                    sudo bash -c " if [[ -f
                    /usr/local/qualys/cloud-agent/bin/qualys-cloud-agent.sh ]];
                    then /usr/local/qualys/cloud-agent/bin/qualys-cloud-agent.sh
                    ActivationId={activationKey} CustomerId={customerId} ; else
                    /Applications/QualysCloudAgent.app/Contents/MacOS/qualys-cloud-agent.sh
                    ActivationId={activationKey} CustomerId={customerId} ; fi"
                    <CopyButton
                      text={`sudo bash -c " if [[ -f /usr/local/qualys/cloud-agent/bin/qualys-cloud-agent.sh ]]; then /usr/local/qualys/cloud-agent/bin/qualys-cloud-agent.sh ActivationId=${activationKey} CustomerId=${customerId} ; else /Applications/QualysCloudAgent.app/Contents/MacOS/qualys-cloud-agent.sh ActivationId=${activationKey} CustomerId=${customerId} ; fi"`}
                    />
                  </p>
                </code>
              </li>
              <li className="d-flex">
                Note: The Activation Key will vary depending on which
                environment the Cloud Agent is being deployed.
              </li>
            </ol>
          </div>
        ),
        downloadUrl: agentInstallerMAC,
      },
      macos_arm64: {
        text: (
          <div className="d-flex flex-column" style={{ gap: '5px' }}>
            <h4 style={{ margin: 0, padding: 0 }}>
              Mac (.pkg) - ARM64 - {companyName} - {selectedActivationKey.label}
            </h4>
            <p>OS X / macOS</p>
            <ol>
              <li>Download the agent installer</li>
              <li>Copy QualysCloudAgent.pkg to the host you want to monitor</li>
              <li>Install the Cloud Agent Package</li>
              <li>
                Open Finder -&gt;Application -&gt; Click on the Qualys App and
                click the app to select “terminal from this folder”.
              </li>
              <li>Enter the following command (Sudo access required):</li>
              <li className="d-flex">
                <code>
                  <p>
                    sudo
                    /Applications/QualysCloudAgent.app/Contents/MacOS/qualys-cloud-agent.sh
                    ActivationId={activationKey} CustomerId={customerId}
                    <CopyButton
                      text={`sudo /Applications/QualysCloudAgent.app/Contents/MacOS/qualys-cloud-agent.sh ActivationId=${activationKey} CustomerId=${customerId}`}
                    />
                  </p>
                </code>
              </li>
              <li className="d-flex">
                Note: The Activation Key will vary depending on which
                environment the Cloud Agent is being deployed.
              </li>
            </ol>
          </div>
        ),
        downloadUrl: agentInstallerMAC_ARM64,
      },
      linux_rpm: {
        text: (
          <div className="d-flex flex-column" style={{ gap: '5px' }}>
            <h4 style={{ margin: 0, padding: 0 }}>
              Linux x64 (.rpm) - {companyName} - {selectedActivationKey.label}
            </h4>
            <p>
              Red Hat Enterprise Linux, CentOS, Fedora, OpenSUSE, SUSE
              Enterprise Linux, Amazon Linux, Oracle Enterprise Linux
            </p>
            <ol>
              <li>Download the agent installer</li>
              <li>Copy QualysCloudAgent.rpm to the host you want to monitor</li>
              <li>Enter the following command (Sudo access required):</li>
              <li className="d-flex">
                <code>
                  <p>
                    sudo rpm -ivh QualysCloudAgent.rpm
                    <br />
                    sudo /usr/local/qualys/cloud-agent/bin/qualys-cloud-agent.sh
                    ActivationId={activationKey} CustomerId={customerId}
                    <CopyButton
                      text={`sudo rpm -ivh QualysCloudAgent.rpm\nsudo /usr/local/qualys/cloud-agent/bin/qualys-cloud-agent.sh ActivationId=${activationKey} CustomerId=${customerId}`}
                    />
                  </p>
                </code>
              </li>
              <li className="d-flex">
                Note: The Activation Key will vary depending on which
                environment the Cloud Agent is being deployed.
              </li>
            </ol>
          </div>
        ),
        downloadUrl: agentInstallerLinuxRPM,
      },
      linux_rpm_arm64: {
        text: (
          <div className="d-flex flex-column" style={{ gap: '5px' }}>
            <h4 style={{ margin: 0, padding: 0 }}>
              Linux ARM64 (.rpm) - {companyName} - {selectedActivationKey.label}
            </h4>
            <p>
              Red Hat Enterprise Linux, CentOS, Fedora, OpenSUSE, SUSE
              Enterprise Linux, Amazon Linux, Oracle Enterprise Linux
            </p>
            <ol>
              <li>Download the agent installer</li>
              <li>Copy QualysCloudAgent.rpm to the host you want to monitor</li>
              <li>Enter the following command (Sudo access required):</li>
              <li className="d-flex">
                <code>
                  <p>
                    sudo rpm -ivh QualysCloudAgent.rpm
                    <br />
                    sudo /usr/local/qualys/cloud-agent/bin/qualys-cloud-agent.sh
                    ActivationId={activationKey} CustomerId={customerId}
                    <CopyButton
                      text={`sudo rpm -ivh QualysCloudAgent.rpm\nsudo /usr/local/qualys/cloud-agent/bin/qualys-cloud-agent.sh ActivationId=${activationKey} CustomerId=${customerId}`}
                    />
                  </p>
                </code>
              </li>
              <li className="d-flex">
                Note: The Activation Key will vary depending on which
                environment the Cloud Agent is being deployed.
              </li>
            </ol>
          </div>
        ),
        downloadUrl: agentInstallerLinuxRPM_ARM64,
      },
      linux_deb: {
        text: (
          <div className="d-flex flex-column" style={{ gap: '5px' }}>
            <h4 style={{ margin: 0, padding: 0 }}>
              Linux x64 (.deb) - {companyName} - {selectedActivationKey.label}
            </h4>
            <p>Debian, Ubuntu</p>
            <ol>
              <li>Download the agent installer</li>
              <li>Copy QualysCloudAgent.deb to the host you want to monitor</li>
              <li>Enter the following command (Sudo access required):</li>
              <li className="d-flex">
                <code>
                  <p>
                    sudo dpkg --install QualysCloudAgent.deb
                    <br />
                    sudo /usr/local/qualys/cloud-agent/bin/qualys-cloud-agent.sh
                    ActivationId={activationKey} CustomerId={customerId}
                    <CopyButton
                      text={`sudo dpkg --install QualysCloudAgent.deb\nsudo /usr/local/qualys/cloud-agent/bin/qualys-cloud-agent.sh ActivationId=${activationKey} CustomerId=${customerId}`}
                    />
                  </p>
                </code>
              </li>
              <li className="d-flex">
                Note: The Activation Key will vary depending on which
                environment the Cloud Agent is being deployed.
              </li>
            </ol>
          </div>
        ),
        downloadUrl: agentInstallerLinuxDEB,
      },
      linux_deb_arm64: {
        text: (
          <div className="d-flex flex-column" style={{ gap: '5px' }}>
            <h4 style={{ margin: 0, padding: 0 }}>
              Linux ARM64 (.deb) - {companyName} - {selectedActivationKey.label}
            </h4>
            <p>Debian, Ubuntu</p>
            <ol>
              <li>Download the agent installer</li>
              <li>Copy QualysCloudAgent.deb to the host you want to monitor</li>
              <li>Enter the following command (Sudo access required):</li>
              <li className="d-flex">
                <code>
                  <p>
                    sudo dpkg --install QualysCloudAgent.deb
                    <br />
                    sudo /usr/local/qualys/cloud-agent/bin/qualys-cloud-agent.sh
                    ActivationId={activationKey} CustomerId={customerId}
                    <CopyButton
                      text={`sudo dpkg --install QualysCloudAgent.deb\nsudo /usr/local/qualys/cloud-agent/bin/qualys-cloud-agent.sh ActivationId=${activationKey} CustomerId=${customerId}`}
                    />
                  </p>
                </code>
              </li>
              <li className="d-flex">
                Note: The Activation Key will vary depending on which
                environment the Cloud Agent is being deployed.
              </li>
            </ol>
          </div>
        ),
        downloadUrl: agentInstallerLinuxDEB_ARM64,
      },
      aix: {
        text: (
          <div className="d-flex flex-column" style={{ gap: '5px' }}>
            <h4 style={{ margin: 0, padding: 0 }}>
              AIX - {companyName} - {selectedActivationKey.label}
            </h4>
            <p>IBM AIX</p>
            <ol>
              <li>Download the agent installer</li>
              <li>Copy QualysCloudAgent.aix to the host you want to monitor</li>
              <li>Enter the following command (Sudo access required):</li>
              <li className="d-flex">
                <code>
                  <p>
                    sudo gzip -fd QualysCloudAgent.bff.gz &gt;
                    QualysCloudAgent.bff
                    <br />
                    sudo installp -acXd . qualys-cloud-agent.rte
                    <br />
                    sudo /opt/qualys/cloud-agent/bin/qualys-cloud-agent.sh
                    ActivationId={`{${activationKey}}`} CustomerId=
                    {`{${customerId}}`}
                    <CopyButton
                      text={`sudo gzip -fd QualysCloudAgent.bff.gz > QualysCloudAgent.bff\nsudo installp -acXd . qualys-cloud-agent.rte\nsudo /usr/local/qualys/cloud-agent/bin/qualys-cloud-agent.sh ActivationId={${activationKey}} CustomerId={${customerId}}`}
                    />
                  </p>
                </code>
              </li>
              <li className="d-flex">
                Note: The Activation Key will vary depending on which
                environment the Cloud Agent is being deployed.
              </li>
            </ol>
          </div>
        ),
        downloadUrl: agentInstallerAIX,
      },
      bsd: {
        text: (
          <div className="d-flex flex-column" style={{ gap: '5px' }}>
            <h4 style={{ margin: 0, padding: 0 }}>
              BSD - {companyName} - {selectedActivationKey.label}
            </h4>
            <p>BSD Systems</p>
            <ol>
              <li>Download the agent installer</li>
              <li>Copy QualysCloudAgent.bsd to the host you want to monitor</li>
              <li>Enter the following command (Sudo access required):</li>
              <li className="d-flex">
                <code>
                  <p>
                    sudo pkg install QualysCloudAgent.txz
                    <br />
                    sudo /usr/local/qualys/cloud-agent/bin/qualys-cloud-agent.sh
                    ActivationId={`{${activationKey}}`} CustomerId=
                    {`{${customerId}}`}
                    <CopyButton
                      text={`sudo pkg install QualysCloudAgent.txz\nsudo /usr/local/qualys/cloud-agent/bin/qualys-cloud-agent.sh ActivationId={${activationKey}} CustomerId={${customerId}}`}
                    />
                  </p>
                </code>
              </li>
              <li className="d-flex">
                Note: The Activation Key will vary depending on which
                environment the Cloud Agent is being deployed.
              </li>
            </ol>
          </div>
        ),
        downloadUrl: agentInstallerBSD,
      },
    };

    const currentInstructions = instructions[selectedOS?.value];
    return currentInstructions ? (
      <div className={styles.instructionsContainer}>
        <Row>
          <Col>
            <div className={styles.instructionsText}>
              {currentInstructions.text}
            </div>
          </Col>
        </Row>
        <Row>
          <Col>
            <a
              href={currentInstructions.downloadUrl}
              className={styles.downloadButton}
            >
              Download Installer for {selectedOS.label}
            </a>
          </Col>
        </Row>
      </div>
    ) : null;
  };

  const customSelectStyles = {
    container: (provided) => ({
      ...provided,
      maxWidth: '300px',
      marginBottom: '15px',
    }),
    control: (provided) => ({
      ...provided,
      border: '1px solid #ddd',
      boxShadow: 'none',
      borderRadius: '4px',
      zIndex: 20,
      '&:hover': {
        borderColor: '#007bff',
      },
      cursor: 'pointer',
    }),

    menu: (provided) => ({
      ...provided,
      borderRadius: '4px',
      zIndex: 9999,
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isFocused ? '#007bff' : 'white',
      color: state.isFocused ? 'white' : 'black',
      cursor: 'pointer',
    }),
    singleValue: (provided) => ({
      ...provided,
      color: 'black',
    }),
  };

  return (
    <div className="content">
      <div className={styles.pageTitle}>
        Qualys Cloud Agent | Installation Guide
      </div>
      <div>
        <Row className="mt-4">
          {/* Left Column - Text Content */}
          <Col md={8}>
            {loader ? (
              <div className={styles.loaderWrapper}>
                <Skeleton
                  width="400px"
                  height="30px"
                  style={{ marginTop: '20px' }}
                />
                <Skeleton width="220px" height="15px" />
                <Skeleton
                  width="620px"
                  height="15px"
                  style={{ marginLeft: '30px' }}
                />
                <Skeleton
                  width="560px"
                  height="15px"
                  style={{ marginLeft: '30px' }}
                />
                <Skeleton
                  width="480px"
                  height="15px"
                  style={{ marginLeft: '30px' }}
                />
                <Skeleton
                  width="400px"
                  height="15px"
                  style={{ marginLeft: '30px' }}
                />
                <Skeleton
                  width="360px"
                  height="15px"
                  style={{ marginLeft: '30px' }}
                />
                <Skeleton
                  width="300px"
                  height="15px"
                  style={{ marginLeft: '30px' }}
                />
                <Skeleton
                  width="150px"
                  height="40px"
                  style={{ marginTop: '30px' }}
                />
              </div>
            ) : (
              <div className={styles.contentPanel}>
                <p className={styles.subTitle}>
                  Installation Instructions for Qualys Cloud Agent
                </p>

                <div className={styles.requirementsSection}>
                  <h5>Requirements</h5>
                  <ul>
                    <li>
                      <p>
                        To install the agent, you must have local administrator
                        privileges on your host.
                      </p>
                    </li>
                    <li>
                      <p>
                        Your host must be able to reach the Qualys Cloud
                        Platform or the Qualys Private Cloud Platform over HTTPS
                        port 443.
                      </p>
                    </li>
                    <li>
                      <p>
                        The agent installer can be downloaded by first selecting
                        the desired OS-variant, and Activation Key (i.e., for
                        designating the type of host).
                      </p>
                    </li>
                  </ul>
                </div>

                <p className="text-justify">
                  As part of your subscription your company gets access to
                  deploy the Qualys Cloud Agent on an unlimited number of
                  servers and workstations. The Qualys Cloud Agent provides
                  instant visibility into security, compliance, and remediation
                  efforts. Securing modern IT infrastructure requires instant
                  visibility of IT assets with up-to-date asset configuration
                  data for continuous security, compliance, and threat
                  detection. The Qualys Cloud Agent is specifically designed to
                  have minimal impact on the system and the network. It normally
                  consumes {'<'}
                  2% of CPU resources, peaking at 5% during normal operation.
                  It's self-updating and self-healing, keeping itself up to date
                  with no need to reboot.
                </p>

                <p className="text-justify">
                  The Qualys Cloud Agents pushes asset data every 2 hours and
                  vulnerability data every 4 hours automatically to the Cloud
                  Platform. From a security and compliance perspective, this
                  makes for a potent combination with the Qualys Cloud Agent
                  reporting host-based vulnerability data every 4 hours. The
                  Qualys Cloud Agent can be installed on any host, such as a
                  laptop, desktop, server, or virtual machine—on premises or in
                  the cloud. This service provides real-time vulnerability
                  management. This service will provide real-time vulnerability
                  management across all servers and workstations.
                </p>

                <div className="d-flex align-items-center">
                  <h4 style={{ margin: '0', padding: '0' }}>Features</h4>
                  <a
                    href="https://www.qualys.com/cloud-agent"
                    target="_blank"
                    rel="noopener noreferrer"
                    title="More info"
                  >
                    <CallMadeRounded fontSize="small" className="ml-1" />
                  </a>
                </div>

                <ul className="mt-2">
                  <li>
                    <p>Minimal footprint</p>
                  </li>
                  <li>
                    <p>Get 100% coverage of your installed infrastructure</p>
                  </li>
                  <li>
                    <p>
                      Continuously monitor assets for the latest Operating
                      System, Application, and Certificate vulnerabilities
                    </p>
                  </li>
                  <li>
                    <p>
                      Track missing critical patches on each device in real time
                    </p>
                  </li>
                  <li>
                    <p>
                      Embed Cloud Agents in the master images of your cloud
                      servers
                    </p>
                  </li>
                  <ul>
                    <li>
                      <p>
                        Cloud Agents automatically register and track new
                        instances created from the master image
                      </p>
                    </li>
                    <li>
                      <p>No need to implement separate discovery mechanisms</p>
                    </li>
                    <li>
                      <p>
                        Information is always up to date even if virtual
                        workloads are offline
                      </p>
                    </li>
                  </ul>
                </ul>
              </div>
            )}
          </Col>

          {/* Right Column - Dropdowns */}
          <Col md={4}>
            {loader ? (
              <div className={styles.loaderWrapper}>
                <Skeleton width="160px" height="15px" />
                <Skeleton width="300px" height="40px" />
                <Skeleton
                  width="160px"
                  height="15px"
                  style={{ marginTop: '10px' }}
                />
                <Skeleton width="300px" height="40px" />
              </div>
            ) : (
              <div className={styles.leftPanel}>
                <div className={styles.leftPanelHeader}>
                  <h4>Installation Options</h4>
                  {isAdminUserLoggedIn() && (
                    <div className="d-flex justify-content-end">
                      <Button
                        color="primary"
                        size="sm"
                        onClick={toggleCreateKeyModal}
                        className="mb-3 px-4 py-2"
                        style={{
                          padding: '8px 16px',
                        }}
                      >
                        <i className="fa fa-plus"></i> Add Key
                      </Button>
                    </div>
                  )}
                </div>
                <div className={styles.selectionPanel}>
                  <div className="mb-3">
                    <p className="mb-2 ">Select Operating System</p>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <div style={{ width: '300px' }}>
                        <Select
                          id="osSelect"
                          value={selectedOS}
                          onChange={handleOSChange}
                          options={osOptions}
                          placeholder="Select OS"
                          isSearchable
                          styles={customSelectStyles}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="mb-3">
                    <p className="mb-2 ">Select Activation Key</p>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <div style={{ width: '300px' }}>
                        <Select
                          id="activationKeySelect"
                          value={selectedActivationKey}
                          onChange={handleActivationKeyChange}
                          options={agentActivationKeyOptions}
                          placeholder="Select Activation Key"
                          isSearchable
                          styles={customSelectStyles}
                        />
                      </div>
                    </div>
                  </div>

                  {selectedOS && selectedActivationKey && (
                    <div className={styles.leftPanelInstructions}>
                      <p>Installation Instructions</p>
                      <div className={styles.installationInstructions}>
                        <div style={{ maxWidth: '100%', overflowX: 'hidden' }}>
                          {getInstallationInstructions()}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </Col>
        </Row>

        <Row>
          <Col md={12}>
            <Suspense fallback={<></>}>
              <CreateActivationKeyModal
                isOpen={isModalOpen}
                toggle={toggleCreateKeyModal}
                existingKeyNames={existingKeyNames}
                onSuccess={handleKeyCreationSuccess}
              />
            </Suspense>
          </Col>
        </Row>
      </div>
      <ToastContainer />
    </div>
  );
};

const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);

  return (
    <div className={styles.copyButton}>
      <CopyToClipboard
        text={text}
        onCopy={() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 3000);
        }}
      >
        {copied ? (
          <i className="fa fa-check fa-lg" title="Copied to Clipboard" />
        ) : (
          <i className="fa fa-copy fa-lg" title="Copy to Clipboard" />
        )}
      </CopyToClipboard>
    </div>
  );
};

const GuestCAInstallationInstructions = () => {
  return (
    <Suspense fallback={<LoadingScreen />}>
      <ErrorBoundary>
        <GuestCAInstallationInstructionsContent />
      </ErrorBoundary>
    </Suspense>
  );
};

export default GuestCAInstallationInstructions;
